/**
 * "Undercurrent" — original composition for the platformer level
 * "Switchback Vault". 112 BPM, D minor, 32-beat loop: sparse kick, brushy
 * hats, walking Dm–Bb–F–A bass, glassy arpeggio, deep pad. Mellower pacing
 * for exploration.
 */
import type { Track, NoteEvent } from '../../audio/track';

const D1 = 26,
  Bb1 = 34,
  F1 = 29,
  A1 = 33;
const D3 = 50,
  F3 = 53,
  A3 = 57,
  D4 = 62,
  Bb3 = 58,
  C4 = 60,
  E3 = 52,
  A2 = 45;

function beats(n: number, step: number, offset = 0): number[] {
  const out: number[] = [];
  for (let b = offset; b < n; b += step) out.push(b);
  return out;
}

function bassBar(startBeat: number, root: number): NoteEvent[] {
  const out: NoteEvent[] = [];
  const pattern = [0, 7, 12, 7];
  for (let i = 0; i < 8; i++) {
    out.push({
      beat: startBeat + i,
      midi: root + (pattern[i % 4] ?? 0),
      len: 0.9,
      vel: i % 4 === 0 ? 0.8 : 0.55,
    });
  }
  return out;
}

function arp(startBeat: number, notes: number[], beatsLen: number): NoteEvent[] {
  const out: NoteEvent[] = [];
  const steps = beatsLen * 2; // eighth notes — calmer than the action tracks
  for (let i = 0; i < steps; i++) {
    out.push({
      beat: startBeat + i * 0.5,
      midi: notes[i % notes.length] ?? 62,
      len: 0.45,
      vel: 0.4,
    });
  }
  return out;
}

const CHORDS: Array<{ root: number; triad: number[]; pad: number[] }> = [
  { root: D1, triad: [D3, F3, A3, D4], pad: [D3, F3, A3] }, // Dm
  { root: Bb1, triad: [Bb3 - 12, D3 + 12, F3, Bb3], pad: [Bb3 - 12, D3, F3] }, // Bb
  { root: F1, triad: [F3, A3, C4, A3], pad: [F3 - 12, A3 - 12, C4 - 12] }, // F
  { root: A1, triad: [A2 + 12, C4 + 1, E3 + 12, A3], pad: [A2, C4 + 1 - 12, E3] }, // A (C# via +1)
];

const LOOP_BEATS = 32;
const bass: NoteEvent[] = [];
const lead: NoteEvent[] = [];
const pad: NoteEvent[] = [];
CHORDS.forEach((ch, i) => {
  const start = i * 8;
  bass.push(...bassBar(start, ch.root));
  lead.push(...arp(start, ch.triad, 8));
  pad.push(...ch.pad.map((midi) => ({ beat: start, midi, len: 7.5, vel: 0.28 })));
});

export const UNDERCURRENT: Track = {
  id: 'undercurrent',
  title: 'Undercurrent',
  bpm: 112,
  loopBeats: LOOP_BEATS,
  kick: beats(LOOP_BEATS, 2),
  hat: beats(LOOP_BEATS, 1, 0.5),
  snare: beats(LOOP_BEATS, 4, 2),
  bass,
  lead,
  pad,
};
